import { useMemo } from "react";
import { useStageByStageId } from "react-migration/domains/sites/pipeline/store/hooks";
import { Recipient } from "react-migration/domains/sites/typings";
import { useTranslation } from "react-migration/lib";

type TableTitleProps = {
  loading: boolean;
  recipients: Recipient[] | null;
  selectedRecipientIds: string[] | null;
  selectedStageId: string | null;
};

export const TableTitle = ({
  loading,
  recipients,
  selectedRecipientIds,
  selectedStageId,
}: TableTitleProps) => {
  const { t } = useTranslation();
  const stage = useStageByStageId(selectedStageId ?? "");

  const recipientsHeaderDetails = useMemo(() => {
    const sitesCountInfo =
      stage?._sites?.totalCount &&
      t("sites.letters.recipients.sites", {
        count: stage._sites.totalCount,
      });

    const eligibleSitesCountInfo = t("sites.letters.recipients.eligible_sites", {
      count: recipients?.filter((recipient) => recipient.isGranted).length,
    });

    const recipientsCountInfo = t("sites.letters.recipients.owners", {
      count: recipients?.length,
    });

    const selectedRecipientsInfo = t("sites.letters.recipients.selected", {
      count: selectedRecipientIds?.length,
    });

    return [
      stage && sitesCountInfo,
      stage && eligibleSitesCountInfo,
      recipientsCountInfo,
      selectedRecipientIds?.length && selectedRecipientsInfo,
    ]
      .filter(Boolean) // filtering out falsy values if letter creation is not bulk send
      .join(" \u2022 "); // adding a bullet point between the values
  }, [recipients, selectedRecipientIds, stage, t]);

  return (
    <div
      data-testid="recipients-table-title"
      className="atlas-p-4 atlas-flex atlas-flex-col atlas-gap-y-1"
    >
      <span className="atlas-flex atlas-text-base atlas-font-semibold">
        {t("sites.letters.recipients")}
      </span>
      {!loading && (
        <span className="atlas-flex atlas-text-content-secondary">{recipientsHeaderDetails}</span>
      )}
    </div>
  );
};
